import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper, Box, Typography, FormControlLabel, Switch, CircularProgress, Alert, Button, Stack, Divider,
} from '@mui/material';
import { settingsApi, runtimeConfigApi } from '../services/api';

/** Read by the backend's PaymentsPolicy. Absent ⇒ in-app payments ON for that type. */
export const SERVICE_KEY = 'booking.settlement.offline'; // "true" = service OFF (inverted)
export const STORE_KEY = 'payments.store.enabled';       // "false" = store OFF

const ROWS = [
  {
    id: 'service',
    label: 'Service providers',
    key: SERVICE_KEY,
    inverted: true,
    effectiveField: 'servicePaymentsEnabled',
    note: 'Changes how new quote acceptances settle. Bookings already accepted keep their mode.',
  },
  {
    id: 'store',
    label: 'Store providers',
    key: STORE_KEY,
    inverted: false,
    effectiveField: 'storePaymentsEnabled',
    note: 'Blocks creating new store orders and starting checkout. Paid orders are unaffected.',
  },
];

/** Exactly the backend's parse (Kotlin toBooleanStrictOrNull): only "true" / "false" count. */
const parseStrict = (raw) => (raw === 'true' ? true : raw === 'false' ? false : null);

/** What the admin saved for a row: { on, source }. Malformed counts as absent. */
function savedState(row, raw) {
  if (raw === null) return { on: true, source: 'default' };
  const parsed = parseStrict(raw);
  if (parsed === null) return { on: true, source: `default (saved value "${raw}" is invalid)` };
  return { on: row.inverted ? !parsed : parsed, source: 'admin setting' };
}

/**
 * In-app payments per provider type (spec 2026-10-25). PAYMENTS_ENABLED is a ceiling these
 * cannot raise, but they stay editable while it is off so they can be set before it is enabled.
 */
export default function PaymentsToggles() {
  const [raw, setRaw] = useState({ service: null, store: null }); // null = absent
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingRow, setSavingRow] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [cfgError, setCfgError] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Only a 404 means "absent"; any other failure is reported, never read as the default.
      const read = (key) => settingsApi.getByKey(key).then(
        (s) => (s && typeof s.value === 'string' ? s.value : null),
        (e) => { if (e && e.status === 404) return null; throw e; },
      );
      const cfgPromise = runtimeConfigApi.get().then((c) => c, () => null);
      const [service, store, config] = await Promise.all([read(SERVICE_KEY), read(STORE_KEY), cfgPromise]);
      setRaw({ service, store });
      setLoadFailed(false);
      const wellTyped = config
        && typeof config.paymentsEnabled === 'boolean'
        && typeof config.servicePaymentsEnabled === 'boolean'
        && typeof config.storePaymentsEnabled === 'boolean';
      setCfg(wellTyped ? config : null);
      setCfgError(!wellTyped);
    } catch (e) {
      setLoadFailed(true);
      setError('Could not load the payment settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const locked = loading || savingRow !== null || loadFailed || cfgError;
  const envOff = cfg !== null && cfg.paymentsEnabled === false;

  const handleToggle = async (row, checked) => {
    if (locked) return;
    const previous = raw[row.id];
    const value = String(row.inverted ? !checked : checked);
    setRaw((r) => ({ ...r, [row.id]: value })); // optimistic
    setSavingRow(row.id);
    setError('');
    try {
      await settingsApi.upsert(row.key, value, 'BOOLEAN');
      await load();
    } catch (e) {
      setRaw((r) => ({ ...r, [row.id]: previous })); // revert
      setError('Failed to update the setting');
    } finally {
      setSavingRow(null);
    }
  };

  const handleReset = async (row) => {
    setSavingRow(row.id);
    setError('');
    try {
      await settingsApi.delete(row.key);
      await load();
    } catch (e) {
      setError('Failed to reset the setting');
    } finally {
      setSavingRow(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">In-app payments</Typography>
        {envOff && (
          <Alert severity="info">
            In-app payments are off on this server (PAYMENTS_ENABLED). Saved values apply once it is enabled.
          </Alert>
        )}
        {cfgError && (
          <Alert severity="warning">
            Could not load the server's payment config; the switches are locked until it loads.
          </Alert>
        )}
        {ROWS.map((row, i) => {
          const saved = savedState(row, raw[row.id]);
          const effective = cfg ? cfg[row.effectiveField] === true : null;
          return (
            <Box key={row.id}>
              {i > 0 && <Divider sx={{ my: 1 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={saved.on}
                      onChange={(e) => handleToggle(row, e.target.checked)}
                      disabled={locked}
                    />
                  )}
                  label={`${row.label}: in-app payments`}
                />
                {savingRow === row.id && <CircularProgress size={16} />}
                <Button
                  size="small"
                  aria-label={`Reset ${row.label} to default`}
                  onClick={() => handleReset(row)}
                  disabled={loading || savingRow !== null || loadFailed || raw[row.id] === null}
                >
                  Reset to default
                </Button>
              </Box>
              {!loadFailed && (
                <Typography variant="caption" color="text.secondary" component="div">
                  {`Saved: ${saved.on ? 'on' : 'off'} (${saved.source})`}
                  {effective !== null && ` · Effective: ${effective ? 'on' : 'off'}${envOff ? ' (PAYMENTS_ENABLED is off)' : ''}`}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" component="div">{row.note}</Typography>
            </Box>
          );
        })}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </Paper>
  );
}
